import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { X, Users, Plus, Trash2, ChevronRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubBrokerManager = ({ broker, allBrokers, onClose, onUpdate }) => {
  const [subBrokerIds, setSubBrokerIds] = useState(broker.sub_broker_ids || []);
  const [saving, setSaving] = useState(false);
  const [descendants, setDescendants] = useState([]);

  // All brokers except the current one
  const availableBrokers = allBrokers.filter(b => b.id !== broker.id);
  const currentSubBrokers = availableBrokers.filter(b => subBrokerIds.includes(b.id));
  const addableBrokers = availableBrokers.filter(b => !subBrokerIds.includes(b.id));

  useEffect(() => {
    const fetchDescendants = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/users/${broker.id}/sub-brokers`);
        setDescendants(res.data.all_descendant_ids || []);
      } catch (err) {
        console.error('Error fetching descendants:', err);
      }
    };
    if (subBrokerIds.length > 0) fetchDescendants();
  }, [broker.id, subBrokerIds]);

  const addSubBroker = (brokerId) => {
    setSubBrokerIds(prev => [...prev, brokerId]);
  };

  const removeSubBroker = (brokerId) => {
    setSubBrokerIds(prev => prev.filter(id => id !== brokerId));
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/users/${broker.id}/sub-brokers`, {
        sub_broker_ids: subBrokerIds,
      });
      onUpdate?.();
      onClose();
    } catch (err) {
      console.error('Error saving sub-brokers:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="sub-broker-manager-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Manage Broker Team</h2>
            <p className="text-sm text-slate-500">{broker.name} {broker.company_name ? `(${broker.company_name})` : ''}</p>
          </div>
          <button onClick={onClose} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Current Sub-Brokers */}
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
              <Users className="h-4 w-4" />
              Direct Sub-Brokers ({currentSubBrokers.length})
            </h3>
            {currentSubBrokers.length === 0 ? (
              <p className="text-sm text-slate-400 py-3 text-center">No sub-brokers assigned</p>
            ) : (
              <div className="space-y-2">
                {currentSubBrokers.map(b => {
                  const hasOwnSubs = (b.sub_broker_ids || []).length > 0;
                  return (
                    <div key={b.id} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-slate-800">{b.name}</p>
                        <p className="text-xs text-slate-500">{b.email}{b.company_name ? ` • ${b.company_name}` : ''}</p>
                        {hasOwnSubs && (
                          <span className="text-xs text-indigo-600 flex items-center gap-0.5 mt-0.5">
                            <ChevronRight className="h-3 w-3" /> Has {b.sub_broker_ids.length} sub-broker(s)
                          </span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeSubBroker(b.id)}
                        className="h-7 w-7 p-0 text-red-400 hover:text-red-600"
                        data-testid={`remove-sub-${b.id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add Sub-Broker */}
          {addableBrokers.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add Sub-Broker
              </h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {addableBrokers.map(b => (
                  <div key={b.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{b.name}</p>
                      <p className="text-xs text-slate-500">{b.email}{b.company_name ? ` • ${b.company_name}` : ''}</p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addSubBroker(b.id)}
                      className="h-7 px-2 text-blue-600 border-blue-300 hover:bg-blue-50"
                      data-testid={`add-sub-${b.id}`}
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Visibility info */}
          {descendants.length > 0 && (
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-xs text-indigo-700">
              <strong>{broker.name}</strong> can see applications from {descendants.length} broker(s) in their team tree.
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="save-sub-brokers-btn">
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubBrokerManager;
